import { Theme } from '@salecycle/ui-component-library';
import styled from 'styled-components';

export interface AlertProps {
  /** Optional class name to appy to alert element - allows for style extension */
  className?: string;
  /** The heading text */
  heading?: string;
  /** Add a custom element to the end of the heading. */
  headingElement?: JSX.Element;
  /** Event fired when the alert is closed. */
  onClose?: () => void;
  /** The style variation of the alert. One of "info" (default), "warning", "error", "success" */
  variation?: AlertVariation;
  /** Alternative background is shown if this is true. */
  altBackground?: boolean;
  /** Maximum width of alert. Defaults to 800px. */
  maxWidth?: string;
}

export type AlertVariation = 'info' | 'warning' | 'error' | 'success';

export const getErrorColorFromType = (variation: string, theme: Theme) => {
  switch (variation) {
    case 'warning':
      return theme.vars.newColor.orange2;
    case 'error':
      return theme.vars.newColor.red2;
    case 'success':
      return theme.vars.newColor.green2;
    case 'info':
    default:
      return theme.vars.newColor.darkGS2;
  }
};

export const getErrorIconFromType = (variation: string) => {
  switch (variation) {
    case 'warning':
      return 'exclamation-triangle';
    case 'error':
      return 'ban';
    case 'success':
      return 'check';
    case 'info':
    default:
      return 'info-circle';
  }
};

export const AlertElement = styled.div<AlertProps>`
  background-color: ${props => getBackgroundColor(props)};
  min-width: 250px;
  max-width: ${props => props.maxWidth};
  font-weight: 600;
  text-align: left;
  position: relative;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  border-left: none;
  margin-left: 5px;

  &:after {
    content: '';
    width: 5px;
    height: calc(100% + 2px);
    background-color: ${({ theme, variation }) => getErrorColorFromType(variation, theme)};
    position: absolute;
    left: -5px;
    top: -1px;
  }
`;

export const AlertHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  justify-content: space-between;
  width: 100%;
`;

export const AlertHeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const AlertHeader = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.vars.newColor.darkGS1};
  display: flex;
  font-size: 20px;
  padding-left: 20px;
  padding-right: 45px;
  text-align: center;
`;

export const AlertText = styled.div`
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
  text-align: left;
  padding-right: 20px;
  padding-left: 75px;
  padding-bottom: 15px;
  color: ${({ theme }) => theme.vars.newColor.darkGS1};
`;

export const CloseButtonElement = styled.button`
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  margin-right: 10px;
  padding: 10px;
`;

export const IconSpan = styled.span`
  padding-left: 20px;
  padding-top: 9px;
  padding-bottom: 9px;
`;

function getBackgroundColor(props) {
  return props.altBackground ? props.theme.vars.color.grey1 : 'transparent';
}
